import React, { useEffect, useState } from "react"
import { useMarginValuation } from "../hooks"
import { useBrands } from "../../../hooks/useBrands"
import { useSiteBrands } from "../../../hooks/useSiteBrands"
import { useMarginValuationRawData } from "../hooks/useMarginValuationRawData"
import { SiteOption } from "../../../types/SiteOption"
import { BrandOption } from "../../../types/BrandOption"
import { computeSaveTarget, ModuleVOConfigScope } from "../moduleVoNavigation"
import { mergeMargin } from "../utils/marginValuationUtils"
import UnsavedChangesAlert from "./UnsavedChangesAlert"
import ScopeSelectors from "./ScopeSelectors"

declare const window: any

interface Props
{
    mode: 'edit' | 'config'
    scope: ModuleVOConfigScope
    brand?: string | null
    dealershipIds?: number[]
}

/**
 * Une seule valeur (marge %), pour global, site(s), marque(s), ou une combinaison. Affichage à
 * deux lignes Global/Site seulement en mode "un seul site, sans marque" (isSiteMode) ; sinon une
 * seule ligne (globalConfig) qui contient soit la vraie valeur enregistrée pour la combinaison,
 * soit une fusion calculée (marge max, actif si l'un des deux l'est) — cf. l'effet de calcul plus
 * bas, même logique que VariableFees.tsx.
 */
export default function MarginValuation({ mode, scope, brand = null, dealershipIds = [] }: Props) {
    const {
        sites,
        globalConfig,
        setGlobalConfig,
        siteConfig,
        setSiteConfig,
        selectedSite,
        setSelectedSite,
        hasChanges,
        submitting,
        loading,
        isSiteMode,
        siteIsModified,
        updateGlobal,
        updateSite,
        save,
    } = useMarginValuation()

    const allBrandsData = useBrands(sites, scope === 'brand')
    const siteBrands = useSiteBrands(scope === 'site' ? selectedSite : null)
    const { data: rawData, loading: rawDataLoading, refetch: refetchRawData } = useMarginValuationRawData()

    const [ selectedSites, setSelectedSites ] = useState<SiteOption[]>([])
    const [ selectedBrands, setSelectedBrands ] = useState<BrandOption[]>([])
    const [ marginConflict, setMarginConflict ] = useState(false)
    const [ activeConflict, setActiveConflict ] = useState(false)

    // Pré-remplissage en mode édition à partir des paramètres de la ligne d'origine (Overview).
    useEffect(() => {
        if (mode !== 'edit') {
            return
        }

        if (scope === 'site' && dealershipIds.length > 0) {
            const site = sites.find(s => s.value === dealershipIds[0])
            if (site) {
                setSelectedSite(site)
                setSelectedSites([ site ])
            }
        }

        if (scope === 'brand') {
            const brandOption = allBrandsData.brands.find(b => b.value === brand)
            setSelectedBrands(brandOption ? [ brandOption ] : [])
            setSelectedSites(sites.filter(s => dealershipIds.includes(s.value)))
        }
    }, [ mode, scope, brand, dealershipIds, sites, allBrandsData ])

    // En mode config + portée "site" : un seul site sélectionné, SANS marque en configuration
    // avancée, est édité en direct via le hook (fetch réel de ce site), comme avant. Dès qu'il y
    // a plusieurs sites ou une marque avancée, on laisse selectedSite à null : sinon le fetch
    // interne du hook (asynchrone) écraserait plus tard les valeurs fusionnées ci-dessous.
    useEffect(() => {
        if (mode !== 'config' || scope !== 'site') {
            return
        }
        setSelectedSite(selectedSites.length === 1 && selectedBrands.length === 0 ? selectedSites[0] : null)
    }, [ mode, scope, selectedSites, selectedBrands ])

    // Dans tous les autres cas (plusieurs sites, plusieurs marques, ou une entité + configuration
    // avancée sur l'autre dimension), on calcule la valeur à afficher à partir de rawData plutôt
    // qu'avec la config réelle d'un seul site (qui, elle, passe par le fetch direct du hook).
    // Nécessaire aussi bien en config qu'en édition (scope 'brand' — marque seule ou site+marque
    // — n'est jamais couvert par le fetch direct du hook, qui ne sait fetcher que par site seul).
    useEffect(() => {
        if (mode !== 'config' && mode !== 'edit') {
            return
        }

        const hasSites = selectedSites.length > 0
        const hasBrands = selectedBrands.length > 0

        const skipMerge = scope === 'site'
            ? !hasSites || (selectedSites.length === 1 && !hasBrands)
            : scope === 'brand'
                ? !hasBrands
                : true

        if (skipMerge) {
            setMarginConflict(false)
            setActiveConflict(false)
            return
        }

        // Une combinaison site+marque peut déjà avoir sa propre ligne en base (un enregistrement
        // précédent visant exactement ce site ET cette marque à la fois) : dans ce cas on
        // l'affiche telle quelle, sans conflit — la fusion site-seul + marque-seule ci-dessous ne
        // sert qu'à prévisualiser une combinaison encore jamais enregistrée telle quelle.
        const exactCombo = hasSites && hasBrands
            ? rawData.find(r =>
                r.dealership_id != null && selectedSites.some(s => s.value === r.dealership_id) &&
                r.brand != null && selectedBrands.some(b => b.value === r.brand),
            )
            : undefined

        if (exactCombo) {
            const margin = String(exactCombo.margin / 100)
            setGlobalConfig({ margin, isActive: exactCombo.is_active, originalMargin: margin, originalIsActive: exactCombo.is_active })
            setMarginConflict(false)
            setActiveConflict(false)
            return
        }

        // Une combinaison site+marque doit fusionner les DEUX contributions (chacune avec repli
        // sur le global si elle n'a pas cette ligne) — jamais une dimension au détriment de
        // l'autre (avant : la marque écrasait complètement le site en scope 'site', et
        // inversement en scope 'brand').
        const entityRows = [
            ...selectedSites.map(s => rawData.find(r => r.dealership_id === s.value && r.brand === null)),
            ...selectedBrands.map(b => rawData.find(r => r.brand === b.value && r.dealership_id === null)),
        ]

        const merged = mergeMargin(globalConfig, entityRows)
        setGlobalConfig(merged.config)
        setMarginConflict(merged.marginConflict)
        setActiveConflict(merged.activeConflict)
        // selectedSite est volontairement inclus (mais pas globalConfig, cf. plus haut) : quand il
        // retombe à null (site retiré, ou passage à plusieurs sites/marques), l'effet interne du
        // hook recharge la config globale seule sur le rendu suivant ; cette dépendance force cet
        // effet-ci à repasser derrière et à réappliquer la fusion correcte.
    }, [ mode, scope, selectedSites, selectedBrands, rawData, selectedSite ])

    useEffect(() => {
        window.$('[data-toggle="tooltip"]').tooltip('dispose')
        window.$('[data-toggle="tooltip"]').tooltip()
    })

    const saveTarget = computeSaveTarget(selectedSites, selectedBrands)
    const saveValue = isSiteMode ? (siteConfig ?? globalConfig) : globalConfig
    const hasBothDimensions = selectedSites.length > 0 && selectedBrands.length > 0
    const conflictScopeLabel = hasBothDimensions
        ? 'le site et la marque sélectionnés'
        : selectedSites.length > 1 ? 'les sites sélectionnés' : 'les marques sélectionnées'
    const conflictOverwriteLabel = hasBothDimensions
        ? 'le site et la marque'
        : selectedSites.length > 1 ? 'tous les sites' : 'toutes les marques'

    // En config, sans marque/site choisi, il n'y a pas encore de cible : ce qui serait affiché
    // est la config globale, qu'on ne veut ni montrer ni pouvoir modifier depuis cette page.
    const hasSelection = scope === 'global' || (scope === 'site' ? selectedSites.length > 0 : selectedBrands.length > 0)
    const wantsTable = mode === 'edit' || hasSelection

    // On n'affiche le tableau qu'une fois tout ce dont on a besoin chargé (sites, marques si
    // nécessaires, rawData, config globale/site) : sinon on voit d'abord la config globale
    // (fetch initial du hook) avant qu'elle ne soit remplacée par celle du site/de la marque.
    const dataReady = sites.length > 0
        && !rawDataLoading
        && !loading
        && (scope !== 'brand' || !allBrandsData.loading)
    const showTable = wantsTable && dataReady

    return (
        <div className="container-fluid mb-2">
            <ScopeSelectors
                scope={scope}
                disabled={mode === 'edit'}
                sites={sites}
                allBrandsData={allBrandsData}
                siteBrands={siteBrands}
                selectedSites={selectedSites}
                onSitesChange={setSelectedSites}
                selectedBrands={selectedBrands}
                onBrandsChange={setSelectedBrands}
            />
            <div className="row">
                <div className="col-md-6">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">Configurez la marge appliquée lors de la cotation</span>
                        <div>
                            {showTable && (hasChanges || mode === 'config') && <UnsavedChangesAlert hasChanges={hasChanges} submitting={submitting} onSubmit={() => save(saveTarget, saveValue).then(refetchRawData)}/>}
                        </div>
                    </div>

                    {!wantsTable && (
                        <p className="text-muted mb-2">
                            <small>
                                <i className="fa fa-info-circle mr-1"></i>
                                Sélectionnez au moins {scope === 'site' ? 'un site' : 'une marque'} pour configurer la marge.
                            </small>
                        </p>
                    )}

                    {wantsTable && !dataReady && (
                        <p className="text-muted mb-2">
                            <small>
                                <i className="fa fa-spinner fa-spin mr-1"></i>
                                Chargement...
                            </small>
                        </p>
                    )}

                    {showTable && <table className="table table-bordered" style={{ maxWidth: '500px' }}>
                        <thead>
                        <tr>
                            <th style={{ width: '60px' }}>Actif</th>
                            <th>Marge (%)</th>
                        </tr>
                        </thead>
                        <tbody>
                        {isSiteMode && globalConfig && <>
                            <tr>
                                <td colSpan={2} className="text-muted py-1 px-2" style={{ backgroundColor: '#f8f9fa', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.05em' }}>
                                    CONFIGURATION GLOBALE
                                </td>
                            </tr>
                            <tr style={{ backgroundColor: '#fafafa' }}>
                                <td className="text-center align-middle text-muted">
                                    {globalConfig.isActive ? 'Actif' : 'Inactif'}
                                </td>
                                <td className="align-middle text-muted">
                                    {globalConfig.margin} %
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} className="text-muted py-1 px-2" style={{ backgroundColor: '#f8f9fa', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.05em' }}>
                                    CONFIGURATION DU SITE
                                </td>
                            </tr>
                            <tr>
                                <td className="text-center align-middle" style={{ backgroundColor: '#fff' }}>
                                    <input
                                        type="checkbox"
                                        checked={siteConfig?.isActive ?? globalConfig.isActive}
                                        onChange={(e) => updateSite('isActive', e.target.checked)}
                                    />
                                    {mode === 'config' && activeConflict && (
                                        <i
                                            className="fa fa-info-circle text-warning ml-1"
                                            data-toggle="tooltip"
                                            data-trigger="hover"
                                            data-placement="top"
                                            data-html="true"
                                            title={`Valeurs différentes selon ${conflictScopeLabel} — actif retenu.`}
                                        ></i>
                                    )}
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={siteConfig?.margin ?? ''}
                                        onChange={(e) => updateSite('margin', e.target.value)}
                                        placeholder={`${globalConfig.margin} (global)`}
                                        step="0.01"
                                        min={0}
                                    />
                                    {mode === 'config' && marginConflict && (
                                        <i
                                            className="fa fa-info-circle text-warning ml-1"
                                            data-toggle="tooltip"
                                            data-trigger="hover"
                                            data-placement="top"
                                            data-html="true"
                                            title={`Valeurs différentes selon ${conflictScopeLabel} — marge la plus élevée retenue. L'enregistrement écrasera la valeur pour ${conflictOverwriteLabel} sélectionné(e)s.`}
                                        ></i>
                                    )}
                                    {!siteConfig && <small className="text-muted">Hérité du global</small>}
                                </td>
                            </tr>
                        </>}

                        {!isSiteMode && globalConfig && (
                            <tr>
                                <td className="text-center align-middle">
                                    <input
                                        type="checkbox"
                                        checked={globalConfig.isActive}
                                        onChange={(e) => updateGlobal('isActive', e.target.checked)}
                                    />
                                    {mode === 'config' && activeConflict && (
                                        <i
                                            className="fa fa-info-circle text-warning ml-1"
                                            data-toggle="tooltip"
                                            data-trigger="hover"
                                            data-placement="top"
                                            data-html="true"
                                            title={`Valeurs différentes selon ${conflictScopeLabel} — actif retenu.`}
                                        ></i>
                                    )}
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={globalConfig.margin}
                                        onChange={(e) => updateGlobal('margin', e.target.value)}
                                        placeholder="—"
                                        step="0.01"
                                        min={0}
                                    />
                                    {mode === 'config' && marginConflict && (
                                        <i
                                            className="fa fa-info-circle text-warning ml-1"
                                            data-toggle="tooltip"
                                            data-trigger="hover"
                                            data-placement="top"
                                            data-html="true"
                                            title={`Valeurs différentes selon ${conflictScopeLabel} — marge la plus élevée retenue. L'enregistrement écrasera la valeur pour ${conflictOverwriteLabel} sélectionné(e)s.`}
                                        ></i>
                                    )}
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>}
                </div>
            </div>
        </div>
    )
}
